import React, { useRef, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { User, ShoppingBag, Heart, LogOut, Settings, Star, Zap, Award, ChevronRight, ChevronLeft, Package, Edit2, Check, X, Camera, MessageSquare, Trash2, Eye, EyeOff, Mail } from 'lucide-react';
import gsap from 'gsap';
import { logout, logoutUser, updateUserSuccess, updateUserProfile, deleteAccount, toggleNewsletter } from '../redux/slice/auth.slice';
import { toggleWishlistDrawer } from '../redux/slice/user.slice';
import { fetchUserReviews, deleteReview, updateReview } from '../redux/slice/review.slice';
import { fetchGames } from '../redux/slice/game.slice';
import LogoutModal from '../components/common/LogoutModal';

const ProfileValidationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email format').required('Email address is required'),
    oldPassword: Yup.string().test('old-password-required', 'Old password is required to change password', function (value) {
        if (this.parent.newPassword && !value) {
            return false;
        }
        return true;
    }),
    newPassword: Yup.string().min(6, 'Password must be at least 6 characters long'),
    confirmPassword: Yup.string()
        .test('passwords-match', 'Passwords must match', function (value) {
            return !this.parent.newPassword || this.parent.newPassword === value;
        })
});

const ReviewEditValidationSchema = Yup.object().shape({
    rating: Yup.number().min(1, 'Rating must be at least 1').max(5, 'Rating cannot exceed 5').required('Rating is required'),
    comment: Yup.string().min(5, 'Comment must be at least 5 characters long').required('Comment is required')
});

export default function Profile() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, loading: sliceLoading } = useSelector((s) => s.auth);
    const { cart, wishlist } = useSelector((s) => s.userSlice);
    const { games } = useSelector((s) => s.game);
    const { userReviews } = useSelector((s) => s.review);

    const [isEditing, setIsEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'reviews'
    const [reviewPage, setReviewPage] = useState(1);
    const reviewsPerPage = 10;

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [selectedReview, setSelectedReview] = useState(null);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [hoverEditRating, setHoverEditRating] = useState(0);
    const [deleteConfirmReviewId, setDeleteConfirmReviewId] = useState(null);
    const [isTogglingNewsletter, setIsTogglingNewsletter] = useState(false);

    const handleToggleNewsletter = async () => {
        if (isTogglingNewsletter) return;
        const currentSubscribed = user?.newsletterEnabled !== false;
        const newSubscribed = !currentSubscribed;

        // Optimistically update local user state for instant switch animation
        if (user) {
            dispatch(updateUserSuccess({ ...user, newsletterEnabled: newSubscribed }));
        }

        setIsTogglingNewsletter(true);
        try {
            await dispatch(toggleNewsletter(newSubscribed)).unwrap();
            toast.success(newSubscribed ? "Successfully subscribed to newsletter!" : "Successfully unsubscribed from newsletter.", {
                style: {
                    background: '#070710',
                    color: '#00f0ff',
                    border: '1px solid rgba(0, 240, 255, 0.2)',
                    fontSize: '11px',
                }
            });
        } catch (err) {
            // Rollback optimistic update on error
            if (user) {
                dispatch(updateUserSuccess({ ...user, newsletterEnabled: currentSubscribed }));
            }
            toast.error(err || "Failed to update newsletter settings");
        } finally {
            setIsTogglingNewsletter(false);
        }
    };

    useEffect(() => {
        if (user?._id || user?.id) {
            dispatch(fetchUserReviews(user._id || user.id));
        }
    }, [user, dispatch]);

    useEffect(() => {
        if (!games || games.length === 0) {
            dispatch(fetchGames({ limit: 100 }));
        }
    }, [dispatch, games]);

    const STATUS_OPTIONS = [
        { id: 'Online', label: 'Online', color: 'text-cyber-green', bg: 'bg-cyber-green/10', border: 'border-cyber-green/20', dotBg: 'bg-cyber-green', pulse: true },
        { id: 'Away', label: 'Away', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', dotBg: 'bg-amber-500', pulse: false },
        { id: 'Busy', label: 'Busy', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20', dotBg: 'bg-red-500', pulse: false },
        { id: 'Offline', label: 'Offline', color: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/20', dotBg: 'bg-gray-400', pulse: false }
    ];

    const currentStatusId = user?.status || 'Online';
    const currentStatus = STATUS_OPTIONS.find(s => s.id === currentStatusId) || STATUS_OPTIONS[0];

    const headerRef = useRef(null);
    const statsRef = useRef(null);
    const editPanelRef = useRef(null);

    useEffect(() => {
        gsap.fromTo(headerRef.current,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
        );
        gsap.fromTo(statsRef.current,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, delay: 0.2, ease: 'power3.out' }
        );
    }, []);

    useEffect(() => {
        if (isEditing && editPanelRef.current) {
            gsap.fromTo(editPanelRef.current,
                { opacity: 0, height: 0, scaleY: 0.9 },
                { opacity: 1, height: 'auto', scaleY: 1, duration: 0.5, ease: 'power4.out' }
            );
        }
    }, [isEditing]);

    const handleLogout = () => {
        setShowLogoutModal(true);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleSaveProfile = async (values, { setSubmitting, resetForm }) => {
        try {
            const userId = user._id || user.id;

            await dispatch(updateUserProfile({
                userId,
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
                photo: user?.photo || '',
                selectedFile,
                status: values.status,
                newsletterEnabled: values.newsletterEnabled
            })).unwrap();

            toast.success("Profile updated successfully!", {
                style: {
                    background: '#070710',
                    color: '#00f0ff',
                    border: '1px solid rgba(0, 240, 255, 0.2)',
                    fontSize: '11px',
                }
            });
            setSelectedFile(null);
            setIsEditing(false);
            resetForm({
                values: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    status: values.status,
                    newsletterEnabled: values.newsletterEnabled
                }
            });
        } catch (error) {
            console.error("Profile update error:", error);
            toast.error(error || "Failed to save profile details", {
                style: {
                    background: '#070710',
                    color: '#ff007a',
                    border: '1px solid rgba(255, 0, 122, 0.2)',
                    fontSize: '11px',
                }
            });
        } finally {
            setSubmitting(false);
        }
    };



    const cartTotal = cart.reduce((total, item) => {
        const plat = item.platform || 'windows';
        const price = item.game?.platforms?.[plat]?.price !== undefined
            ? item.game.platforms[plat].price
            : (item.game?.price || 0);
        return total + price * item.quantity;
    }, 0);
    const wishlistDetailed = useSelector((s) => s.userSlice.wishlistDetailed || []);
    const wishlistGames = wishlistDetailed.length > 0
        ? wishlistDetailed
        : games.filter((g) => wishlist.includes(g._id || g.id));

    const STATS = [
        { label: 'CART ITEMS', value: cart.reduce((a, i) => a + i.quantity, 0), icon: ShoppingBag, color: 'text-cyber-blue', border: 'border-cyber-blue/20', bg: 'bg-cyber-blue/10' },
        { label: 'WISHLIST', value: wishlist.length, icon: Heart, color: 'text-cyber-pink', border: 'border-cyber-pink/20', bg: 'bg-cyber-pink/10' },
        { label: 'CART VALUE', value: `$${cartTotal.toFixed(2)}`, icon: Zap, color: 'text-cyber-cyan', border: 'border-cyber-cyan/20', bg: 'bg-cyber-cyan/10' },
        // { label: 'LEVEL', value: '42', icon: Award, color: 'text-cyber-purple', border: 'border-cyber-purple/20', bg: 'bg-cyber-purple/10' },
    ];

    const MENU = [
        ...(user?.role && user.role.toLowerCase() === 'admin' ? [{
            label: 'Admin Controls',
            subtitle: 'System & store command',
            icon: Award,
            path: '/admin/dashboard',
            color: 'text-cyber-pink',
            iconBg: 'bg-cyber-pink/15 text-cyber-pink border-cyber-pink/30',
            glow: 'hover:border-cyber-pink/50 hover:shadow-[0_0_20px_rgba(255,0,122,0.2)]'
        }] : []),
        {
            label: 'My Library',
            subtitle: 'Accessible games & keys',
            icon: Package,
            path: '/library',
            color: 'text-cyber-blue',
            iconBg: 'bg-cyber-blue/15 text-cyber-blue border-cyber-blue/30',
            glow: 'hover:border-cyber-blue/50 hover:shadow-[0_0_20px_rgba(59,130,246,0.2)]'
        },
        {
            label: 'Order History',
            subtitle: 'Transactions & invoices',
            icon: ShoppingBag,
            path: '/order-history',
            color: 'text-cyber-cyan',
            iconBg: 'bg-cyber-cyan/15 text-cyber-cyan border-cyber-cyan/30',
            glow: 'hover:border-cyber-cyan/50 hover:shadow-[0_0_20px_rgba(0,240,255,0.2)]'
        },
        // {
        //     label: 'My Wishlist',
        //     subtitle: 'Saved & tracked titles',
        //     icon: Heart,
        //     path: '/wishlist',
        //     color: 'text-cyber-pink',
        //     iconBg: 'bg-cyber-pink/15 text-cyber-pink border-cyber-pink/30',
        //     glow: 'hover:border-cyber-pink/50 hover:shadow-[0_0_20px_rgba(255,0,122,0.2)]',
        //     badge: wishlist.length
        // },
        {
            label: 'Newsletter Settings',
            subtitle: 'Drops & release radar',
            icon: Mail,
            color: 'text-cyber-cyan',
            iconBg: 'bg-cyber-cyan/15 text-cyber-cyan border-cyber-cyan/30',
            glow: 'hover:border-cyber-cyan/40 hover:shadow-[0_0_20px_rgba(0,240,255,0.15)]'
        },
        {
            label: 'Delete Account',
            subtitle: 'Permanent profile erase',
            icon: Trash2,
            onClick: () => setShowDeleteConfirm(true),
            color: 'text-red-400',
            iconBg: 'bg-red-500/15 text-red-400 border-red-500/30',
            glow: 'hover:border-red-500/50 hover:shadow-[0_0_20px_rgba(239,68,68,0.2)]'
        },
    ];

    return (
        <div className="min-h-screen pt-24 pb-12 sm:pt-32 sm:pb-32 relative">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-cyber-blue/4 rounded-full blur-[140px] pointer-events-none"></div>

            <div className="container mx-auto space-y-8">

                {/* Profile header card */}
                <div ref={headerRef} className="relative rounded-2xl z-20 overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #0c0c18 0%, #080810 50%, #0a0a16 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 20px 60px rgba(0,0,0,0.5)' }}>
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple opacity-70 rounded-t-2xl"></div>
                    <div className="absolute top-0 left-0 w-64 h-64 bg-cyber-blue/5 rounded-full blur-[80px] pointer-events-none"></div>
                    <div className="absolute inset-0 rounded-2xl opacity-[0.02] pointer-events-none"
                        style={{ backgroundImage: 'radial-gradient(circle, rgba(0,240,255,0.8) 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>

                    <div className="relative z-10 p-5 sm:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0 group cursor-pointer" onClick={() => setIsEditing(true)}>
                            <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-cyber-blue/40 shadow-[0_0_20px_rgba(0,240,255,0.2)] relative">
                                {user?.photo
                                    ? <img src={user.photo} alt="avatar" className="w-full h-full object-cover" />
                                    : <div className="w-full h-full bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 flex items-center justify-center">
                                        <User className="w-10 h-10 text-cyber-blue/60" />
                                    </div>}
                                <div className="absolute inset-0 bg-cyber-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Camera className="w-6 h-6 text-cyber-cyan" />
                                </div>
                            </div>
                            <div className={`absolute bottom-0 right-1 w-6 h-6 rounded-full ${currentStatus.dotBg} border-2 border-cyber-black flex items-center justify-center`}>
                                <div className={`w-2 h-2 rounded-full ${currentStatus.dotBg} ${currentStatus.pulse ? 'animate-pulse' : ''}`}></div>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 text-center md:text-left min-w-0">
                            <p className="text-[10px] font-cyber font-medium text-cyber-cyan tracking-wider mb-1 uppercase">User Profile</p>
                            <h1 className="font-cyber font-medium text-lg sm:text-xl md:text-2xl text-white truncate">
                                {user ? `${user.firstName} ${user.lastName}` : 'Player One'}
                            </h1>
                            <p className="text-gray-500 text-xs sm:text-sm font-mono mt-1 truncate">{user?.email || 'player@cubegames.io'}</p>
                            <div className="flex items-center justify-center md:justify-start gap-2 mt-4 relative">
                                <span className="px-3 py-1 rounded-full bg-cyber-blue/10 border border-cyber-blue/20 text-cyber-blue text-[9px] sm:text-[10px] font-cyber font-medium uppercase tracking-widest whitespace-nowrap">
                                    {user?.role || 'PLAYER'}
                                </span>

                                <span className={`px-3 py-1 rounded-full ${currentStatus.bg} border ${currentStatus.border} ${currentStatus.color} text-[9px] sm:text-[10px] font-cyber font-medium flex items-center gap-1.5 whitespace-nowrap hover:bg-white/[0.04] transition-all cursor-pointer`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${currentStatus.dotBg} ${currentStatus.pulse ? 'animate-pulse' : ''}`}></span>
                                    {currentStatus.label}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="w-full md:w-auto flex flex-col sm:flex-row md:flex-col lg:flex-row items-stretch sm:items-center justify-center md:justify-end gap-2.5 border-t md:border-t-0 border-white/5 pt-6 md:pt-0">
                            <button
                                type="button"
                                onClick={() => setIsEditing(!isEditing)}
                                className={`group w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-xs font-cyber font-medium uppercase tracking-wider transition-all duration-300 cursor-pointer ${
                                    isEditing
                                        ? 'bg-cyber-blue text-black border border-cyber-blue font-bold shadow-[0_0_15px_rgba(0,240,255,0.3)]'
                                        : 'bg-white/5 hover:bg-cyber-blue/15 border border-white/10 hover:border-cyber-blue/50 text-gray-300 hover:text-cyber-blue shadow-sm'
                                }`}
                            >
                                <Edit2 className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                                <span>{isEditing ? "Close Edit" : "Edit Profile"}</span>
                            </button>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="group w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/35 hover:border-red-500/70 text-red-400 hover:text-red-300 text-xs font-cyber font-semibold uppercase tracking-wider transition-all duration-300 cursor-pointer shadow-[0_0_12px_rgba(239,68,68,0.12)] hover:shadow-[0_0_20px_rgba(239,68,68,0.25)]"
                            >
                                <LogOut className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
                {isEditing && (
                    <div ref={editPanelRef} className="relative rounded-2xl p-4 md:p-6 overflow-hidden space-y-2 md:space-y-6"
                        style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #050508 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }}>
                        <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue to-cyber-cyan opacity-80"></div>
                        <p className="text-[10px] font-cyber font-medium text-cyber-blue tracking-wider">Edit profile settings</p>
                        <Formik
                            initialValues={{
                                firstName: user?.firstName || '',
                                lastName: user?.lastName || '',
                                email: user?.email || '',
                                oldPassword: '',
                                newPassword: '',
                                confirmPassword: '',
                                status: user?.status || 'Online'
                            }}
                            validationSchema={ProfileValidationSchema}
                            onSubmit={handleSaveProfile}
                            enableReinitialize
                        >
                            {({ errors, touched, isSubmitting }) => (
                                <Form className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">First Name</label>
                                            <Field type="text" name="firstName"
                                                className={`w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all
                                                ${errors.firstName && touched.firstName ? 'border-red-500/50' : ''}`} />
                                            {errors.firstName && touched.firstName && (
                                                <p className="text-[10px] text-red-500">{errors.firstName}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Last Name</label>
                                            <Field type="text" name="lastName"
                                                className={`w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all
                                                ${errors.lastName && touched.lastName ? 'border-red-500/50' : ''}`} />
                                            {errors.lastName && touched.lastName && (
                                                <p className="text-[10px] text-red-500">{errors.lastName}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Email Address</label>
                                            <Field type="email" name="email" disabled
                                                className={`w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all
                                            ${errors.email && touched.email ? 'border-red-500/50' : ''}`} />
                                            {errors.email && touched.email && (
                                                <p className="text-[10px] text-red-500">{errors.email}</p>
                                            )}
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Profile Status</label>
                                            <Field as="select" name="status"
                                                className="w-full px-4 py-3 rounded-xl text-xs md:text-sm font-cyber text-white bg-[#0e0e1a]/80 border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all cursor-pointer">
                                                <option value="Online" className="bg-[#0e0e1a] text-cyber-green">🟢 Online</option>
                                                <option value="Away" className="bg-[#0e0e1a] text-amber-500">🟡 Away</option>
                                                <option value="Busy" className="bg-[#0e0e1a] text-red-500">🔴 Busy</option>
                                                <option value="Offline" className="bg-[#0e0e1a] text-gray-400">⚪ Offline</option>
                                            </Field>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Upload Profile Picture</label>
                                        <div className="relative">
                                            <input type="file" accept="image/*" onChange={handleFileChange} id="avatar-upload" className="hidden" />
                                            <label htmlFor="avatar-upload" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/10 hover:border-cyber-blue/40 text-xs text-gray-400 hover:text-white bg-white/[0.02] cursor-pointer transition-all">
                                                <Camera className="w-4 h-4 text-cyber-blue" />
                                                {selectedFile ? selectedFile.name : "BROWSE IMAGE FILE"}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="border-t border-white/5 pt-6 mt-6">
                                        <p className="text-[10px] font-cyber font-medium text-cyber-cyan tracking-wider mb-4">Security Credentials</p>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-cyber font-medium text-gray-500 tracking-wide uppercase">Old Password</label>
                                                <div className="relative">
                                                    <Field type={showOldPassword ? "text" : "password"} name="oldPassword" placeholder="Current password"
                                                        className={`w-full pl-4 pr-10 py-3 rounded-xl text-xs font-sans text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all placeholder:text-gray-700
                                                        ${errors.oldPassword && touched.oldPassword ? 'border-red-500/50' : ''}`} />
                                                    <button type="button" onClick={() => setShowOldPassword(!showOldPassword)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none">
                                                        {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                                {errors.oldPassword && touched.oldPassword && (
                                                    <p className="text-[10px] text-red-500">{errors.oldPassword}</p>
                                                )}
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-cyber font-medium text-gray-500 tracking-wide uppercase">New Password</label>
                                                <div className="relative">
                                                    <Field type={showNewPassword ? "text" : "password"} name="newPassword" placeholder="Leave blank to keep unchanged"
                                                        className={`w-full pl-4 pr-10 py-3 rounded-xl text-xs font-sans text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all placeholder:text-gray-700
                                                        ${errors.newPassword && touched.newPassword ? 'border-red-500/50' : ''}`} />
                                                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none">
                                                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                                {errors.newPassword && touched.newPassword && (
                                                    <p className="text-[10px] text-red-500">{errors.newPassword}</p>
                                                )}
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-cyber font-medium text-gray-500 tracking-wide uppercase">Confirm Password</label>
                                                <div className="relative">
                                                    <Field type={showConfirmPassword ? "text" : "password"} name="confirmPassword" placeholder="Re-enter password"
                                                        className={`w-full pl-4 pr-10 py-3 rounded-xl text-xs font-sans text-white bg-white/[0.04] border border-white/10 focus:border-cyber-blue/50 focus:outline-none transition-all placeholder:text-gray-700
                                                        ${errors.confirmPassword && touched.confirmPassword ? 'border-red-500/50' : ''}`} />
                                                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none">
                                                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                                {errors.confirmPassword && touched.confirmPassword && (
                                                    <p className="text-[10px] text-red-500">{errors.confirmPassword}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row justify-end gap-3 pt-2">
                                        <button type="button" onClick={() => setIsEditing(false)}
                                            className="order-2 sm:order-1 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 hover:text-white text-xs font-cyber font-medium transition-all">
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={isSubmitting || sliceLoading}
                                            className="order-1 sm:order-2 cyber-btn flex items-center justify-center gap-2 !px-6 !py-3 disabled:opacity-50"
                                        >
                                            {isSubmitting || sliceLoading ? "SAVING..." : <><Check className="w-4 h-4" /> Save Changes</>}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}

                {/* Stats grid */}
                <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-3 gap-3.5 sm:gap-4">
                    {STATS.map(({ label, value, icon: Icon, color, border, bg }) => {
                        const isWishlist = label === 'WISHLIST';
                        const isCart = label === 'CART ITEMS' || label === 'CART VALUE';
                        const linkTo = isWishlist ? "/wishlist" : (isCart ? "/cart" : null);

                        const hoverBorderColor = isWishlist
                            ? 'hover:border-cyber-pink/35'
                            : label === 'CART ITEMS'
                                ? 'hover:border-cyber-blue/35'
                                : 'hover:border-cyber-cyan/35';

                        const ambientGlow = isWishlist
                            ? 'from-cyber-pink/[0.03]'
                            : label === 'CART ITEMS'
                                ? 'from-cyber-blue/[0.03]'
                                : 'from-cyber-cyan/[0.03]';

                        const bottomAccent = isWishlist
                            ? 'group-hover:via-cyber-pink/35'
                            : label === 'CART ITEMS'
                                ? 'group-hover:via-cyber-blue/35'
                                : 'group-hover:via-cyber-cyan/35';

                        const cardClasses = `relative rounded-2xl p-3.5 sm:p-4.5 bg-[#0a0a14]/90 border border-white/[0.08] ${hoverBorderColor} transition-all duration-300 group hover:bg-white/[0.02] hover:-translate-y-0.5 flex items-center justify-between cursor-pointer overflow-hidden shadow-sm`;

                        const CardInner = (
                            <>
                                {/* Low Ambient Glow on Hover */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${ambientGlow} via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />

                                {/* Left: Icon + Value & Label */}
                                <div className="relative z-10 flex items-center gap-3 sm:gap-3.5 min-w-0 flex-1">
                                    <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl ${bg} border ${border} flex items-center justify-center shrink-0 group-hover:scale-105 transition-all duration-300`}>
                                        <Icon className={`w-5 h-5 ${color} transition-transform duration-300 group-hover:rotate-3`} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className={`font-cyber font-bold text-lg xs:text-xl sm:text-2xl ${color} tracking-tight leading-none`}>
                                            {value}
                                        </p>
                                        <p className="text-[9px] xs:text-[10px] sm:text-[11px] text-gray-400 font-cyber font-medium tracking-wider uppercase mt-1 truncate group-hover:text-gray-200 transition-colors">
                                            {label}
                                        </p>
                                    </div>
                                </div>

                                {/* Subtle Bottom Accent Line */}
                                <div className={`absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent ${bottomAccent} transition-all duration-300`} />
                            </>
                        );

                        if (linkTo) {
                            return (
                                <Link
                                    key={label}
                                    to={linkTo}
                                    className={cardClasses}
                                >
                                    {CardInner}
                                </Link>
                            );
                        }

                        return (
                            <div
                                key={label}
                                className={cardClasses}
                            >
                                {CardInner}
                            </div>
                        );
                    })}
                </div>

                {/* Tabs + Content Section */}
                <div className="space-y-5">

                    {/* ── Horizontal Tab Bar ───────────────────────────────── */}
                    <div className="grid grid-cols-2 sm:flex sm:items-center gap-1.5 p-1.5 rounded-2xl bg-white/[0.02] border border-white/[0.06]" style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.03)' }}>
                        <button
                            type="button"
                            onClick={() => setActiveTab('overview')}
                            className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-5 sm:py-2.5 rounded-xl text-[9px] sm:text-[10px] font-cyber font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer w-full sm:w-auto ${activeTab === 'overview'
                                ? 'bg-cyber-blue/15 border border-cyber-blue/35 text-cyber-blue shadow-[0_0_18px_rgba(0,200,255,0.18)]'
                                : 'border border-transparent text-gray-400 hover:text-white hover:bg-white/[0.04]'
                                }`}
                        >
                            <User className="w-3.5 h-3.5" />
                            Overview
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('reviews')}
                            className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-5 sm:py-2.5 rounded-xl text-[9px] sm:text-[10px] font-cyber font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer w-full sm:w-auto ${activeTab === 'reviews'
                                ? 'bg-cyber-blue/15 border border-cyber-blue/35 text-cyber-blue shadow-[0_0_18px_rgba(0,200,255,0.18)]'
                                : 'border border-transparent text-gray-400 hover:text-white hover:bg-white/[0.04]'
                                }`}
                        >
                            <MessageSquare className="w-3.5 h-3.5" />
                            My Reviews
                            <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-cyber font-bold ${activeTab === 'reviews' ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30' : 'bg-white/[0.08] text-gray-400'}`}>
                                {userReviews?.length || 0}
                            </span>
                        </button>
                    </div>

                    {/* ── Tab Content ──────────────────────────────────────── */}
                    <div>
                        {activeTab === 'overview' ? (
                            /* Quick Access HUD — Full Width */
                            <div className="relative rounded-2xl overflow-hidden p-3.5 xs:p-4 sm:p-6"
                                style={{ background: 'linear-gradient(135deg, #0c0c18 0%, #080810 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }}>
                                <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple opacity-70"></div>
                                <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 mb-4 sm:mb-6">
                                    <h2 className="text-xs sm:text-sm font-cyber font-semibold text-cyber-blue tracking-wider flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-cyber-blue shrink-0" /> Quick Access
                                    </h2>
                                </div>

                                {/* Action Modules Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
                                        {MENU.map(({ label, subtitle, icon: Icon, path, onClick, iconBg, glow, badge }) => {
                                            const isDelete = label === 'Delete Account';
                                            const isNewsletter = label === 'Newsletter Settings';
                                            const isSubscribed = user?.newsletterEnabled !== false;

                                            const cardClasses = `group relative flex items-center justify-between p-4 rounded-xl border transition-all duration-300 w-full cursor-pointer overflow-hidden ${isDelete
                                                ? 'bg-red-500/[0.04] border-red-500/15 hover:bg-red-500/[0.08] ' + glow
                                                : 'bg-white/[0.025] border-white/[0.06] hover:bg-white/[0.05] ' + glow
                                                }`;

                                            const cardContent = (
                                                <>
                                                    <div className="flex items-center gap-3.5 min-w-0">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-transform duration-300 group-hover:scale-105 shadow-sm ${iconBg}`}>
                                                            <Icon className="w-5 h-5" />
                                                        </div>
                                                        <div className="text-left min-w-0">
                                                            <p className={`text-xs sm:text-sm font-cyber font-bold uppercase tracking-wider truncate transition-colors ${isDelete ? 'text-red-400 group-hover:text-red-300' : 'text-gray-200 group-hover:text-white'
                                                                }`}>
                                                                {label}
                                                            </p>
                                                            <p className="text-[11px] text-gray-500 font-sans truncate mt-0.5">
                                                                {subtitle}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {isNewsletter ? (
                                                        <div className="flex items-center gap-2.5 shrink-0 ml-3 pointer-events-none">
                                                            <span className={`text-[10px] font-cyber font-bold tracking-wider transition-colors duration-200 ${isSubscribed ? 'text-cyber-cyan' : 'text-gray-500'}`}>
                                                                {isSubscribed ? 'ON' : 'OFF'}
                                                            </span>
                                                            <div className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 ${
                                                                isSubscribed ? 'bg-cyber-cyan/30' : 'bg-white/10'
                                                            }`}>
                                                                <div className={`w-4 h-4 rounded-full transition-all duration-200 absolute top-[2px] ${
                                                                    isSubscribed 
                                                                        ? 'left-[18px] bg-cyber-cyan shadow-[0_0_8px_rgba(0,240,255,0.8)]' 
                                                                        : 'left-[2px] bg-gray-400'
                                                                }`} />
                                                            </div>
                                                        </div>
                                                    ) : badge !== undefined ? (
                                                        <div className="flex items-center gap-1.5 shrink-0 ml-3">
                                                            <span className="px-2.5 py-0.5 rounded-md bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink text-[10px] font-cyber font-bold shadow-[0_0_8px_rgba(255,0,122,0.3)]">
                                                                {badge}
                                                            </span>
                                                            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-cyber-pink group-hover:translate-x-0.5 transition-all" />
                                                        </div>
                                                    ) : (
                                                        <div className="shrink-0 ml-3">
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${isDelete
                                                                ? 'bg-red-500/10 text-red-400 group-hover:bg-red-500/20'
                                                                : 'bg-white/[0.04] text-gray-500 group-hover:text-cyber-cyan group-hover:bg-cyber-cyan/10'
                                                                }`}>
                                                                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );

                                            if (isNewsletter) {
                                                return (
                                                    <button
                                                        key={label}
                                                        type="button"
                                                        onClick={handleToggleNewsletter}
                                                        disabled={isTogglingNewsletter}
                                                        className={`${cardClasses} text-left cursor-pointer`}
                                                    >
                                                        {cardContent}
                                                    </button>
                                                );
                                            }
                                            if (path) {
                                                return <Link key={label} to={path} className={cardClasses}>{cardContent}</Link>;
                                            }
                                            return <button key={label} type="button" onClick={onClick} className={cardClasses}>{cardContent}</button>;
                                        })}
                                    </div>
                                </div>
                        ) : (
                            /* My Reviews tab dynamic layout */
                            <div className="relative rounded-2xl overflow-hidden p-3.5 xs:p-4 sm:p-6"
                                style={{ background: 'linear-gradient(135deg, #0c0c18 0%, #080810 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }}>
                                <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple opacity-70"></div>
                                <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 mb-4 sm:mb-6">
                                    <h2 className="text-xs sm:text-sm font-cyber font-semibold text-cyber-blue tracking-wider flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4 text-cyber-blue shrink-0" /> My Submitted Reviews
                                    </h2>
                                    {/* {userReviews && userReviews.length > 0 && (
                                        <span className="text-[9px] sm:text-[10px] font-cyber text-gray-400 uppercase tracking-wider">
                                            {userReviews.length} {userReviews.length === 1 ? 'Review' : 'Reviews'}
                                        </span>
                                    )} */}
                                </div>

                                {!userReviews || userReviews.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                                        <MessageSquare className="w-12 h-12 text-gray-700 mb-4" />
                                        <p className="text-gray-500 text-xs font-cyber">You haven't submitted any reviews yet.</p>
                                        <Link to="/all-games" className="mt-4 px-4 py-2 rounded-xl bg-cyber-blue/10 border border-cyber-blue/20 text-cyber-blue hover:bg-cyber-blue/25 text-[10px] font-cyber transition-all">
                                            Explore Games to Review
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3.5 sm:gap-4">
                                            {userReviews
                                                .slice((reviewPage - 1) * reviewsPerPage, reviewPage * reviewsPerPage)
                                                .map((review) => {
                                                    const title = review.game?.title || review.blog?.title || 'Game Feedback';
                                                    const image = review.game?.cover_image?.lowUrl || review.game?.cover_image?.url || review.game?.image || review.blog?.image?.url || review.blog?.image || null;
                                                    const rating = review.rating || 0;
                                                    const isApproved = review.status === 'Published';
                                                    const isPending = review.status === 'Pending';

                                                    return (
                                                        <div key={review._id || review.id} className="relative rounded-2xl p-3.5 sm:p-4 bg-[#0a0a14]/90 border border-white/[0.08] hover:border-cyber-blue/40 hover:bg-white/[0.03] transition-all duration-300 flex flex-col justify-between group">
                                                            {/* Top Row: Thumbnail + Info + Actions */}
                                                            <div className="flex items-start gap-3 sm:gap-3.5">
                                                                {image && (
                                                                    <img
                                                                        src={image}
                                                                        alt={title}
                                                                        className="w-12 h-16 sm:w-14 sm:h-18 rounded-xl object-cover shrink-0 bg-white/5 border border-white/10 shadow-md group-hover:scale-105 transition-transform"
                                                                    />
                                                                )}
                                                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                                                    {/* Title & Action Buttons */}
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <h3 className="text-xs sm:text-sm font-cyber font-bold text-white truncate group-hover:text-cyber-blue transition-colors">
                                                                            {title}
                                                                        </h3>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedReview(review);
                                                                                    setOpenEditModal(true);
                                                                                }}
                                                                                className="p-1.5 rounded-lg bg-white/5 text-gray-300 hover:text-black hover:bg-cyber-blue border border-white/10 hover:border-cyber-blue transition-all cursor-pointer"
                                                                                title="Edit Review"
                                                                            >
                                                                                <Edit2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setDeleteConfirmReviewId(review._id || review.id)}
                                                                                className="p-1.5 rounded-lg bg-white/5 text-gray-300 hover:text-white hover:bg-red-500/80 border border-white/10 hover:border-red-500 transition-all cursor-pointer"
                                                                                title="Delete Review"
                                                                            >
                                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Rating, Status Badge & Date */}
                                                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                                        <div className="flex items-center gap-0.5">
                                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                                <Star key={star} className={`w-3 h-3 ${star <= rating ? 'text-amber-400 fill-amber-400 filter drop-shadow-[0_0_4px_rgba(251,191,36,0.5)]' : 'text-gray-700'}`} />
                                                                            ))}
                                                                        </div>
                                                                        {/* <span className="text-[10px] font-cyber font-bold text-amber-400">
                                                                            {rating}.0
                                                                        </span> */}
                                                                        {/* <span className={`px-2 py-0.5 rounded text-[8px] font-cyber uppercase tracking-wider font-bold ${isApproved ? 'bg-cyber-green/15 text-cyber-green border border-cyber-green/30' :
                                                                            isPending ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                                                                                'bg-red-500/15 text-red-400 border border-red-500/30'
                                                                            }`}>
                                                                            {review.status || 'Pending'}
                                                                        </span>
                                                                        <span className="text-[10px] font-sans text-gray-400">
                                                                            {new Date(review.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                                                        </span> */}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Full-width Comment Box */}
                                                            <div className="mt-3 p-2.5 sm:p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                                                                <p className="text-gray-300 text-xs font-sans italic leading-relaxed line-clamp-3">
                                                                    {review.comment}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        {/* Pagination Controls */}
                                        {Math.ceil((userReviews?.length || 0) / reviewsPerPage) > 1 && (
                                            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-5 border-t border-white/5">
                                                <p className="text-[10px] xs:text-[11px] sm:text-xs font-cyber text-gray-300 uppercase tracking-wider text-center sm:text-left">
                                                    Showing <span className="text-cyber-blue font-semibold">{(reviewPage - 1) * reviewsPerPage + 1}</span> -{' '}
                                                    <span className="text-cyber-blue font-semibold">
                                                        {Math.min(reviewPage * reviewsPerPage, userReviews.length)}
                                                    </span>{' '}
                                                    of <span className="text-white font-semibold">{userReviews.length}</span> reviews
                                                </p>
                                                <div className="flex items-center gap-1 sm:gap-1.5">
                                                    <button
                                                        type="button"
                                                        disabled={reviewPage === 1}
                                                        onClick={() => setReviewPage((prev) => Math.max(prev - 1, 1))}
                                                        className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-white/[0.02] border border-white/10 hover:border-cyber-blue/30 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-gray-400 transition-all cursor-pointer"
                                                    >
                                                        <ChevronLeft className="w-3.5 h-3.5" />
                                                    </button>

                                                    {(() => {
                                                        const totalPages = Math.ceil((userReviews?.length || 0) / reviewsPerPage);
                                                        const pages = [];

                                                        if (totalPages <= 6) {
                                                            for (let i = 1; i <= totalPages; i++) pages.push(i);
                                                        } else {
                                                            pages.push(1);
                                                            let start = Math.max(2, reviewPage - 1);
                                                            let end = Math.min(totalPages - 1, reviewPage + 1);

                                                            if (reviewPage <= 3) {
                                                                end = 4;
                                                            } else if (reviewPage >= totalPages - 2) {
                                                                start = totalPages - 3;
                                                            }

                                                            if (start > 2) {
                                                                pages.push('ellipsis-left');
                                                            }

                                                            for (let i = start; i <= end; i++) {
                                                                pages.push(i);
                                                            }

                                                            if (end < totalPages - 1) {
                                                                pages.push('ellipsis-right');
                                                            }

                                                            pages.push(totalPages);
                                                        }

                                                        return pages.map((p, idx) => {
                                                            if (p === 'ellipsis-left' || p === 'ellipsis-right') {
                                                                return (
                                                                    <span key={`ellipsis-${idx}`} className="px-1 text-gray-300 font-cyber text-[8px] sm:text-[10px] select-none">
                                                                        ...
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <button
                                                                    key={p}
                                                                    type="button"
                                                                    onClick={() => setReviewPage(p)}
                                                                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-cyber font-bold transition-all cursor-pointer ${reviewPage === p
                                                                        ? 'bg-cyber-blue/20 border border-cyber-blue/40 text-cyber-blue shadow-[0_0_12px_rgba(0,240,255,0.25)]'
                                                                        : 'bg-white/[0.02] border border-white/10 text-gray-400 hover:text-white hover:border-cyber-blue/20'
                                                                        }`}
                                                                >
                                                                    {p}
                                                                </button>
                                                            );
                                                        });
                                                    })()}

                                                    <button
                                                        type="button"
                                                        disabled={reviewPage === Math.ceil((userReviews?.length || 0) / reviewsPerPage)}
                                                        onClick={() => setReviewPage((prev) => Math.min(prev + 1, Math.ceil((userReviews?.length || 0) / reviewsPerPage)))}
                                                        className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-white/[0.02] border border-white/10 hover:border-cyber-blue/30 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-gray-400 transition-all cursor-pointer"
                                                    >
                                                        <ChevronRight className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            <LogoutModal
                isOpen={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onConfirm={() => {
                    dispatch(logout());
                    setShowLogoutModal(false);
                    navigate('/login');
                }}
            />

            {/* Account Deletion Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="relative w-full max-w-md rounded-2xl p-6 overflow-hidden border border-red-500/30"
                        style={{ background: 'linear-gradient(135deg, #100609 0%, #080204 100%)', boxShadow: '0 0 40px rgba(255, 0, 80, 0.2)' }}>
                        <div className="absolute top-0 left-0 w-full h-[3px] bg-red-500"></div>
                        <h3 className="text-sm font-cyber font-medium text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Trash2 className="w-5 h-5" /> Warning: Account Deletion
                        </h3>
                        <p className="text-xs text-gray-300 font-sans mb-6 leading-relaxed">
                            Are you absolutely sure you want to delete your Cube Games account? This will soft-delete your profile, meaning you will lose access to your libraries, purchases, and settings. This action is irreversible.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 text-xs font-cyber transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        const userId = user._id || user.id;
                                        await dispatch(deleteAccount(userId)).unwrap();
                                        toast.success("Account deleted successfully.", {
                                            style: {
                                                background: '#070710',
                                                color: '#ff0055',
                                                border: '1px solid rgba(255, 0, 85, 0.2)',
                                                fontSize: '11px',
                                            }
                                        });
                                        setShowDeleteConfirm(false);
                                        navigate('/login');
                                    } catch (err) {
                                        toast.error(err || "Failed to delete account");
                                    }
                                }}
                                className="px-4 py-2.5 rounded-xl bg-red-600/10 border border-red-600/30 hover:bg-red-600/20 text-red-500 text-xs font-cyber transition-all"
                            >
                                Yes, Delete My Account
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Review Modal */}
            {openEditModal && selectedReview && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="w-full max-w-lg bg-[#0c0c18] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300">
                        {/* Neon Top Decorative Line */}
                        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple"></div>

                        {/* Ambient Glows */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyber-blue/10 rounded-full blur-[60px] pointer-events-none"></div>
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyber-purple/10 rounded-full blur-[60px] pointer-events-none"></div>

                        {/* Close Button */}
                        <button
                            type="button"
                            onClick={() => { setOpenEditModal(false); setSelectedReview(null); }}
                            className="absolute top-5 right-5 p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 rounded-2xl bg-cyber-blue/10 border border-cyber-blue/25 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,240,255,0.15)]">
                                <Edit2 className="w-5 h-5 text-cyber-blue" />
                            </div>
                            <div className="min-w-0 pr-6">
                                <h3 className="text-base sm:text-lg font-cyber font-bold text-white uppercase tracking-wider">
                                    Edit Review
                                </h3>
                                <p className="text-xs text-gray-400 font-sans truncate mt-0.5">
                                    Updating feedback for <span className="text-cyber-blue font-medium">{selectedReview.game?.title || selectedReview.blog?.title || 'Selected Item'}</span>
                                </p>
                            </div>
                        </div>

                        <Formik
                            initialValues={{
                                rating: selectedReview.rating || 5,
                                comment: selectedReview.comment || ''
                            }}
                            validationSchema={ReviewEditValidationSchema}
                            onSubmit={async (values, { setSubmitting }) => {
                                try {
                                    await dispatch(updateReview({
                                        reviewId: selectedReview._id || selectedReview.id,
                                        rating: values.rating,
                                        comment: values.comment
                                    })).unwrap();
                                    toast.success("Review updated successfully! It has been submitted for approval.", {
                                        style: {
                                            background: '#070710',
                                            color: '#00f0ff',
                                            border: '1px solid rgba(0, 240, 255, 0.2)',
                                            fontSize: '11px',
                                        }
                                    });
                                    setOpenEditModal(false);
                                    setSelectedReview(null);
                                    if (user?._id || user?.id) {
                                        dispatch(fetchUserReviews(user._id || user.id));
                                    }
                                } catch (err) {
                                    toast.error(err || "Failed to update review");
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({ values, errors, touched, setFieldValue, isSubmitting }) => {
                                const currentRating = hoverEditRating || values.rating;
                                const ratingLabels = {
                                    5: 'Masterpiece',
                                    4: 'Great',
                                    3: 'Good',
                                    2: 'Mediocre',
                                    1: 'Awful'
                                };

                                return (
                                    <Form className="space-y-4">
                                        {/* Rating selector module */}
                                        <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] flex flex-col items-center gap-2">
                                            <span className="text-[10px] font-cyber font-semibold text-gray-500 uppercase tracking-widest">Score Rating</span>
                                            <div className="flex items-center gap-2">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <button
                                                        key={star}
                                                        type="button"
                                                        onClick={() => setFieldValue('rating', star)}
                                                        onMouseEnter={() => setHoverEditRating(star)}
                                                        onMouseLeave={() => setHoverEditRating(0)}
                                                        className="p-1 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                                                    >
                                                        <Star
                                                            className={`w-7 h-7 sm:w-8 sm:h-8 transition-all ${star <= currentRating
                                                                ? 'text-amber-400 fill-amber-400 filter drop-shadow-[0_0_10px_rgba(251,191,36,0.6)]'
                                                                : 'text-gray-700 hover:text-gray-500'
                                                                }`}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <span className="px-2.5 py-0.5 rounded-md bg-amber-400/10 border border-amber-400/20 text-xs font-cyber font-bold text-amber-400">
                                                    {currentRating} / 5
                                                </span>
                                                <span className="text-[11px] font-sans font-medium text-gray-400">
                                                    • {ratingLabels[currentRating] || 'Good'}
                                                </span>
                                            </div>
                                            {errors.rating && touched.rating && (
                                                <p className="text-[10px] text-red-400 font-sans">{errors.rating}</p>
                                            )}
                                        </div>

                                        {/* Comment textarea */}
                                        <div className="space-y-1.5">
                                            <div className="flex items-center justify-between">
                                                <label className="text-[10px] font-cyber font-semibold text-gray-400 uppercase tracking-widest">
                                                    Review Feedback
                                                </label>
                                                <span className="text-[9px] text-gray-600 font-mono uppercase">
                                                    Min 5 characters
                                                </span>
                                            </div>
                                            <Field
                                                as="textarea"
                                                name="comment"
                                                rows={4}
                                                placeholder="Write your constructive thoughts..."
                                                className={`w-full px-4 py-3 rounded-2xl text-xs sm:text-sm font-sans text-gray-200 bg-white/[0.03] border transition-all placeholder:text-gray-600 focus:outline-none resize-none ${errors.comment && touched.comment
                                                    ? 'border-red-500/50 focus:border-red-500 focus:ring-1 focus:ring-red-500/30'
                                                    : 'border-white/10 focus:border-cyber-blue/60 focus:ring-1 focus:ring-cyber-blue/30'
                                                    }`}
                                            />
                                            {errors.comment && touched.comment && (
                                                <p className="text-[10px] text-red-400 font-sans">{errors.comment}</p>
                                            )}
                                        </div>

                                        {/* Modal Footer Actions */}
                                        <div className="flex gap-3 pt-3">
                                            <button
                                                type="button"
                                                onClick={() => { setOpenEditModal(false); setSelectedReview(null); }}
                                                className="flex-1 py-3 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 text-gray-300 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all cursor-pointer"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="flex-1 py-3 rounded-xl sm:rounded-2xl bg-cyber-blue hover:bg-cyber-blue/90 text-black font-cyber font-bold text-[10px] uppercase tracking-widest shadow-[0_0_20px_rgba(0,240,255,0.25)] hover:shadow-[0_0_25px_rgba(0,240,255,0.4)] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                            >
                                                <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                                                {isSubmitting ? "Saving..." : "Save Changes"}
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            )}

            {/* Review Delete Confirmation Modal */}
            {deleteConfirmReviewId && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="w-full max-w-md bg-[#0c0c18] border border-white/10 rounded-[1.5rem] p-8 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-pink to-cyber-purple"></div>
                        <div className="text-center space-y-6">
                            <div className="w-20 h-20 bg-cyber-pink/10 rounded-3xl flex items-center justify-center mx-auto border border-cyber-pink/20">
                                <Trash2 className="w-10 h-10 text-cyber-pink" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-cyber font-black text-white uppercase tracking-tight">Delete Review?</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">Are you sure you want to delete this review? This action cannot be undone.</p>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setDeleteConfirmReviewId(null)}
                                    className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            await dispatch(deleteReview(deleteConfirmReviewId)).unwrap();
                                            toast.success("Review deleted successfully");
                                            setDeleteConfirmReviewId(null);
                                            if (user?._id || user?.id) {
                                                dispatch(fetchUserReviews(user._id || user.id));
                                            }
                                        } catch (err) {
                                            toast.error(err || "Failed to delete review");
                                        }
                                    }}
                                    className="flex-1 py-4 rounded-2xl bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-pink/30 shadow-[0_0_20px_rgba(255,0,122,0.1)] transition-all"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}